import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Tooltip } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import OverViewChart from '../../../../components/dashboardcomponents/OverViewChart'
import { getKbOverviewStats, getKbRecentActivity } from '../../../../store/features/mvKbOverview'
import { useNavigate } from 'react-router-dom'
import { NewBadge } from '../../../../utils'

import { ReactComponent as Folder } from '../../../../assets/images/ChatImages/folder.svg'
import { ReactComponent as Link } from '../../../../assets/images/link.svg'
import CheckIcon from '@mui/icons-material/Check';
import NoDataFound from '../../../../components/dashboardcomponents/Traffic/NoDataFound'
import { buildExternalKbUrl, selectBusinessSlug } from '../../../../utils/kbPublicUrl'
import { fetchWorkspaceSettings } from '../../../../store/features/workspaceSettingsApi'
import GroupSelectDropdown from '../../../../components/dashboardcomponents/ProfileComponents/GroupSelectDropdown'
import { useAnalyticsGroupId } from '../../../../hooks/useAnalyticsGroupId'
import { isHeaderInlinePageTitleEnabled } from '../../../../config/featureFlags'
import '../../TicketManagment/style.css'

const Overview = () => {
  const dispatch = useDispatch();
  const business_id = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const businessSlug = useSelector(selectBusinessSlug);
  const { stats, recentActivity, statsLoading, recentLoading } = useSelector((state) => state.kbOverview);
  const navigate = useNavigate();
  const [copiedRowId, setCopiedRowId] = useState(null);
  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();

  useEffect(() => {
    if (business_id && !businessSlug) {
      dispatch(fetchWorkspaceSettings());
    }
  }, [dispatch, business_id, businessSlug]);

  useEffect(() => {
    if (business_id && selectedGroupId) {
      dispatch(getKbOverviewStats({ business_id, group_id: selectedGroupId }));
      dispatch(getKbRecentActivity({ business_id, group_id: selectedGroupId }));
    }
  }, [dispatch, business_id, selectedGroupId]);

  const summaryData = useMemo(() => {
    const data = stats?.data || {};
    return [
      {
        id: 1,
        title: 'Published',
        number: data.published || 0,
        textPercentage: null,
        value: null,
        showTrend: false
      },
      {
        id: 2,
        title: 'Drafts',
        number: data.draft || 0,
        textPercentage: null,
        value: null,
        showTrend: false
      },
      {
        id: 3,
        title: 'Pending Review',
        number: data.pendingReview || 0,
        textPercentage: null,
        value: null,
        showTrend: false
      },
      {
        id: 4,
        title: 'Archived',
        number: data.archived || 0,
        textPercentage: null,
        value: null,
        showTrend: false
      }
    ];
  }, [stats]);

  const renderStatusChip = (status) => {
    const s = (status || '').toLowerCase();

    if (s === 'published') {
      return (
        <Box sx={{
          width: '80px', height: '24px', borderRadius: '5px',
          border: '1px solid #148DFD', background: '#F3FBFF',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#405261', fontFamily: 'Inter', fontSize: '12px', fontWeight: 500, lineHeight: '18px'
        }}>
          Published
        </Box>
      )
    }
    if (s === 'pending review' || s === 'pendingreview') {
      return (
        <Box sx={{
          width: '112px', height: '24px', borderRadius: '5px',
          border: '1px solid #FA8C16', background: '#FFF7E6',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#405261', fontFamily: 'Inter', fontSize: '12px', fontWeight: 500, lineHeight: '18px'
        }}>
          Pending Review
        </Box>
      )
    }

    if (s === 'draft') {

      return (
        <Box sx={{
          width: '56px', height: '24px', borderRadius: '5px',
          border: '1px solid #FADB14', background: '#FFFBE6',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#405261', fontFamily: 'Inter', fontSize: '12px', fontWeight: 500, lineHeight: '18px'
        }}>
          Draft
        </Box>
      )
    }

    if (s === 'archived') {
      return (
        <Box sx={{
          width: '80px', height: '24px', borderRadius: '5px',
          border: '1px solid #FF4D4F', background: '#FFF1F0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#405261', fontFamily: 'Inter', fontSize: '12px', fontWeight: 500, lineHeight: '18px'
        }}>
          Archived
        </Box>
      )
    }

    return (
      <Box sx={{
        padding: '0 8px', height: '24px', borderRadius: '5px',
        border: '1px solid #D9D9D9', background: '#FAFAFA',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: '#405261', fontFamily: 'Inter', fontSize: '12px', fontWeight: 500
      }}>
        {status}
      </Box>
    );
  }

  const handleCopyLink = (e, row) => {
    e.stopPropagation();
    const url = buildExternalKbUrl({
      slug: businessSlug,
      articleId: row._id || row.id,
      groupId: selectedGroupId,
    });
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopiedRowId(row._id || row.id);
    setTimeout(() => setCopiedRowId(null), 2000);
  };

  const isStatsLoading = !selectedGroupId || statsLoading;
  const isRecentLoading = !selectedGroupId || recentLoading;

  return (
    <Box sx={{ padding: { xs: '16px', md: '24px' }, paddingTop: { md: '20px' } }}>
      {/* Legacy in-page title + group — hidden when header shows them (Application V2) */}
      {!isHeaderInlinePageTitleEnabled && (
        <div className="profile-header-row" style={{ marginBottom: '16px' }}>
          <p className="Ticket-text">Knowledge Base</p>
          <GroupSelectDropdown
            onSelect={setSelectedGroupId}
            value={selectedGroupId}
          />
        </div>
      )}

      <Box sx={{ marginBottom: '32px' }}>
        <OverViewChart
          data={summaryData}
          loading={isStatsLoading}
          statsContainerStyle={{ width: '100%' }}
          skeletonCount={4}
        />
      </Box>

      <Typography sx={{ color: '#061522', fontSize: '18px', fontWeight: 500, lineHeight: '18px', marginBottom: '8px' }}>
        Recent Activity
      </Typography>

      <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 0', '& th:first-of-type, & td:first-of-type': { paddingLeft: 0 } }}>

        <TableHead sx={{ padding: '0px', margin: '0px' }}>
          <TableRow sx={{
            '& th': {
              padding: '16px 16px 9px 16px'
            }
          }}>
            <TableCell sx={{ color: '#405261', fontFamily: 'Inter', fontSize: '12px', fontWeight: 400, lineHeight: '18px', borderBottom: '1px solid #E5E9EC', padding: '0px' }}>Title</TableCell>
            <TableCell sx={{ color: '#405261', fontFamily: 'Inter', fontSize: '12px', fontWeight: 400, lineHeight: '18px', borderBottom: '1px solid #E5E9EC' }}>Status</TableCell>
            <TableCell sx={{ color: '#405261', fontFamily: 'Inter', fontSize: '12px', fontWeight: 400, lineHeight: '18px', borderBottom: '1px solid #E5E9EC' }}>Created by</TableCell>
            <TableCell sx={{ color: '#405261', fontFamily: 'Inter', fontSize: '12px', fontWeight: 400, lineHeight: '18px', borderBottom: '1px solid #E5E9EC' }}>Last Updated</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {isRecentLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i} sx={{ height: '64px' }}>
                <TableCell sx={{ borderBottom: '1px solid #E5E9EC', py: '20px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="text" width={200} height={24} sx={{ borderRadius: '6px' }} />
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #E5E9EC', py: '20px' }}>
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '5px' }} />
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #E5E9EC', py: '20px' }}>
                  <Skeleton variant="text" width={120} height={20} />
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #E5E9EC', py: '20px' }}>
                  <Skeleton variant="text" width={100} height={20} />
                </TableCell>
              </TableRow>
            ))
          ) : recentActivity.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} sx={{ border: 'none', p: 0 }}>
                <NoDataFound containerStyle={{ height: '44vh' }} isForKb={true} title="No articles found" description="This segment is empty. Try another one or come back later" />
              </TableCell>
            </TableRow>
          ) : (
            recentActivity.map((row, i) => (
              <TableRow key={row.id || i} onClick={() => navigate(`/kb/article/${row._id || row.id}`)}
                sx={{
                  height: '64px',
                  cursor: 'pointer',
                  maxHeight: '64px',
                  '& td': {
                    py: '0px',
                    height: '64px',
                    boxSizing: 'border-box',
                  },
                  '&:hover': {
                    backgroundColor: '#F7F8F9',
                  }
                }}
              >
                <TableCell sx={{ borderBottom: '1px solid #E5E9EC', py: '20px', minWidth: '582px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Folder />
                    <Typography sx={{ color: '#061522', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, lineHeight: '18px' }}>
                      {row.title}
                    </Typography>
                    {row.visibility?.toLowerCase() === 'public' &&
                      !['pendingreview', 'draft', 'new'].includes(row.status?.toLowerCase()) && (
                        <Tooltip title={copiedRowId === (row._id || row.id) ? "Copied!" : "Copy public link"} arrow>
                          <Box
                            component="div"
                            onClick={(e) => handleCopyLink(e, row)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.7
                              }
                            }}
                          >
                            {copiedRowId === (row._id || row.id) ? (
                              <CheckIcon sx={{ fontSize: 20, color: '#148DFD' }} />
                            ) : (
                              <Link style={{ minWidth: 16, minHeight: 16 }} />
                            )}
                          </Box>
                        </Tooltip>
                      )}
                    {row.isNew && <NewBadge />}
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #E5E9EC', py: '20px' }}>
                  {renderStatusChip(row.status)}
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #E5E9EC', py: '20px' }}>
                  <Typography sx={{ color: '#405261', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, lineHeight: '18px' }}>{row.creator}</Typography>
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #E5E9EC', py: '20px' }}>
                  <Typography sx={{ color: '#405261', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, lineHeight: '18px' }}>{row.updated}</Typography>
                </TableCell>
              </TableRow>
            )))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default Overview
